// City landing page data for /browse/:stateSlug/:citySlug routes.
// Each city corresponds to listings whose `city` field matches one of the
// `cityMatchers` (case-insensitive). Surrounding metro names are listed
// here so a single landing page can capture searches across the whole metro
// (e.g. "MedSpa in NYC" should also surface listings in Brooklyn / Queens).

export interface CityData {
  slug: string;            // URL slug under /browse/:stateSlug/
  name: string;            // Display name ("New York City")
  stateSlug: string;       // Matches StateData.slug
  stateAbbr: string;       // 2-letter abbr ("NY")
  stateName: string;       // "New York"
  /** Listing.city values that should appear on this page (case-insensitive). */
  cityMatchers: string[];
  /** Short SEO description (used in meta + hero). */
  description: string;
  /** Long-form intro paragraph for the page body. */
  intro: string;
  /** Neighborhoods / surrounding areas — rendered as a chip list. */
  neighborhoods: string[];
  /** Top treatments worth highlighting in this market (display strings). */
  topTreatments: string[];
}

export const CITIES: CityData[] = [
  {
    slug: "new-york-city",
    name: "New York City",
    stateSlug: "new-york",
    stateAbbr: "NY",
    stateName: "New York",
    cityMatchers: [
      "New York",
      "New York City",
      "NYC",
      "Manhattan",
      "Brooklyn",
      "Queens",
      "Bronx",
      "Staten Island",
    ],
    description:
      "Find the best MedSpas in New York City. Compare verified Manhattan, Brooklyn, and Queens medical spas offering Botox, fillers, laser, and body contouring — with real reviews, hours, and pricing.",
    intro:
      "New York City has one of the most competitive aesthetic markets in the country, with hundreds of medical spas across Manhattan, Brooklyn, Queens, and the surrounding boroughs. Whether you're booking a quick Botox touch-up between meetings in Midtown, a HydraFacial on the Upper East Side, or RF microneedling in Williamsburg, this directory makes it easy to compare verified NYC providers side by side. Every listing includes treatments offered, hours, location, and patient reviews so you can choose with confidence.",
    neighborhoods: [
      "Manhattan",
      "Upper East Side",
      "Upper West Side",
      "Midtown",
      "SoHo",
      "Tribeca",
      "Chelsea",
      "Williamsburg",
      "Park Slope",
      "Brooklyn Heights",
      "Long Island City",
      "Astoria",
    ],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Lip Fillers",
      "Laser Hair Removal",
      "Microneedling",
      "HydraFacial",
      "CoolSculpting",
      "Morpheus8",
    ],
  },
  {
    slug: "los-angeles",
    name: "Los Angeles",
    stateSlug: "california",
    stateAbbr: "CA",
    stateName: "California",
    cityMatchers: ["Los Angeles", "LA", "West Hollywood", "Hollywood", "Studio City", "Sherman Oaks"],
    description:
      "Browse the best MedSpas in Los Angeles. Compare verified providers in Beverly Hills, West Hollywood, Santa Monica, and the Valley for Botox, fillers, laser, and body contouring.",
    intro:
      "Los Angeles sets the pace for aesthetic medicine — from Beverly Hills injectables to West Hollywood laser specialists and Santa Monica wellness clinics. Browse verified LA-area MedSpas, compare what each one offers, and read patient reviews before you book.",
    neighborhoods: [
      "Beverly Hills",
      "West Hollywood",
      "Santa Monica",
      "Hollywood",
      "Brentwood",
      "Studio City",
      "Sherman Oaks",
      "Pasadena",
      "Downtown LA",
    ],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Lip Fillers",
      "Laser Hair Removal",
      "Morpheus8",
      "CoolSculpting",
      "PRP Injections",
      "Sculptra",
    ],
  },
  {
    slug: "miami",
    name: "Miami",
    stateSlug: "florida",
    stateAbbr: "FL",
    stateName: "Florida",
    cityMatchers: ["Miami", "Miami Beach", "Coral Gables", "Aventura", "Brickell", "Doral"],
    description:
      "Find top-rated MedSpas in Miami. Compare verified providers in Brickell, Coral Gables, Miami Beach, and Aventura for Botox, fillers, laser, and body sculpting.",
    intro:
      "Miami is one of the largest MedSpa markets in the U.S., with a strong focus on body contouring, non-surgical butt lifts, and high-end injectables. Browse verified Miami-area medical spas across Brickell, Coral Gables, Miami Beach, and Aventura with real reviews and full treatment menus.",
    neighborhoods: [
      "Brickell",
      "Miami Beach",
      "Coral Gables",
      "Aventura",
      "Wynwood",
      "Coconut Grove",
      "Doral",
      "Edgewater",
    ],
    topTreatments: [
      "Body Contouring",
      "Butt Lift (Non-Surgical)",
      "Lip Fillers",
      "Botox",
      "Laser Hair Removal",
      "EMSculpt",
      "CoolSculpting",
      "Skin Tightening",
    ],
  },
  {
    slug: "houston",
    name: "Houston",
    stateSlug: "texas",
    stateAbbr: "TX",
    stateName: "Texas",
    cityMatchers: ["Houston", "Sugar Land", "The Woodlands", "Katy", "Pearland"],
    description:
      "Browse verified MedSpas in Houston, TX. Compare top providers in River Oaks, the Galleria, the Heights, and Sugar Land for Botox, fillers, laser, and weight loss.",
    intro:
      "Houston is home to one of the largest concentrations of medical spas in Texas, with strong options across River Oaks, the Galleria, the Heights, and the surrounding suburbs. Compare verified Houston-area MedSpas for injectables, lasers, weight management, and full-service aesthetics.",
    neighborhoods: [
      "River Oaks",
      "Galleria",
      "Memorial",
      "The Heights",
      "Montrose",
      "Sugar Land",
      "The Woodlands",
      "Katy",
    ],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Semaglutide / GLP-1",
      "Medical Weight Loss",
      "Laser Hair Removal",
      "Microneedling",
      "IV Therapy",
      "CoolSculpting",
    ],
  },
  {
    slug: "dallas",
    name: "Dallas",
    stateSlug: "texas",
    stateAbbr: "TX",
    stateName: "Texas",
    cityMatchers: ["Dallas", "Plano", "Frisco", "Highland Park", "University Park", "Irving"],
    description:
      "Find top-rated MedSpas in Dallas, TX. Compare verified providers in Uptown, Highland Park, Plano, and Frisco for Botox, fillers, and laser treatments.",
    intro:
      "Dallas-Fort Worth has a fast-growing MedSpa scene, with leading injectors in Uptown and Highland Park and full-service aesthetic clinics throughout Plano, Frisco, and the surrounding suburbs. Browse verified Dallas-area providers and compare treatments, hours, and reviews.",
    neighborhoods: [
      "Uptown",
      "Highland Park",
      "Preston Hollow",
      "Plano",
      "Frisco",
      "Las Colinas",
      "Lakewood",
      "Bishop Arts",
    ],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Lip Fillers",
      "Morpheus8",
      "Microneedling",
      "Laser Hair Removal",
      "Semaglutide / GLP-1",
      "Sculptra",
    ],
  },
  {
    slug: "austin",
    name: "Austin",
    stateSlug: "texas",
    stateAbbr: "TX",
    stateName: "Texas",
    cityMatchers: ["Austin", "Round Rock", "Cedar Park", "Westlake"],
    description:
      "Browse verified MedSpas in Austin, TX. Find top providers in Downtown, Westlake, South Congress, and Round Rock for Botox, fillers, laser, and wellness.",
    intro:
      "Austin's MedSpa scene blends boutique injectables with wellness-focused clinics, IV therapy, and weight-loss programs. Browse verified Austin-area providers from downtown to Westlake and the surrounding tech corridor.",
    neighborhoods: ["Downtown Austin", "Westlake", "South Congress", "The Domain", "Round Rock", "Cedar Park"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "IV Therapy",
      "Semaglutide / GLP-1",
      "Microneedling",
      "Laser Hair Removal",
      "HydraFacial",
      "PRP Injections",
    ],
  },
  {
    slug: "chicago",
    name: "Chicago",
    stateSlug: "illinois",
    stateAbbr: "IL",
    stateName: "Illinois",
    cityMatchers: ["Chicago", "Oak Park", "Evanston", "Naperville", "Schaumburg"],
    description:
      "Find the best MedSpas in Chicago. Compare verified providers in the Gold Coast, Lincoln Park, the Loop, and the North Shore for Botox, fillers, and laser.",
    intro:
      "Chicago's medical spas span the Magnificent Mile, Gold Coast, Lincoln Park, and the North Shore suburbs. Compare verified Chicago-area providers offering injectables, lasers, body contouring, and wellness services.",
    neighborhoods: ["Gold Coast", "Lincoln Park", "River North", "The Loop", "Wicker Park", "Evanston", "Naperville", "Schaumburg"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Laser Hair Removal",
      "Microneedling",
      "CoolSculpting",
      "HydraFacial",
      "Morpheus8",
      "Chemical Peels",
    ],
  },
  {
    slug: "atlanta",
    name: "Atlanta",
    stateSlug: "georgia",
    stateAbbr: "GA",
    stateName: "Georgia",
    cityMatchers: ["Atlanta", "Buckhead", "Sandy Springs", "Alpharetta", "Marietta", "Roswell"],
    description:
      "Browse verified MedSpas in Atlanta, GA. Compare top providers in Buckhead, Midtown, Sandy Springs, and Alpharetta for Botox, fillers, and body contouring.",
    intro:
      "Atlanta's MedSpa market is anchored by Buckhead and the affluent northern suburbs, with strong demand for injectables, body contouring, and PRP-based treatments. Browse verified Atlanta-area providers and compare what each one offers.",
    neighborhoods: ["Buckhead", "Midtown", "Sandy Springs", "Alpharetta", "Roswell", "Marietta", "Decatur"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Morpheus8",
      "CoolSculpting",
      "Laser Hair Removal",
      "Microneedling",
      "PRP Hair Treatment",
      "Semaglutide / GLP-1",
    ],
  },
  {
    slug: "las-vegas",
    name: "Las Vegas",
    stateSlug: "nevada",
    stateAbbr: "NV",
    stateName: "Nevada",
    cityMatchers: ["Las Vegas", "Henderson", "Summerlin", "North Las Vegas"],
    description:
      "Find top-rated MedSpas in Las Vegas. Compare verified providers in Summerlin, Henderson, and on the Strip for Botox, fillers, laser, and body sculpting.",
    intro:
      "Las Vegas has one of the most active aesthetic markets in the West, with high volumes of injectables and laser treatments. Browse verified Las Vegas-area MedSpas across Summerlin, Henderson, and the Strip corridor.",
    neighborhoods: ["Summerlin", "Henderson", "The Strip", "Downtown Las Vegas", "North Las Vegas"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Lip Fillers",
      "Laser Hair Removal",
      "CoolSculpting",
      "Microneedling",
      "HydraFacial",
      "IV Therapy",
    ],
  },
  {
    slug: "scottsdale",
    name: "Scottsdale",
    stateSlug: "arizona",
    stateAbbr: "AZ",
    stateName: "Arizona",
    cityMatchers: ["Scottsdale", "Paradise Valley", "Fountain Hills"],
    description:
      "Browse top MedSpas in Scottsdale, AZ. Compare verified providers in Old Town, North Scottsdale, and Paradise Valley for Botox, fillers, and laser.",
    intro:
      "Scottsdale is one of the most concentrated MedSpa markets in the Southwest, with a strong reputation for injectables, lasers, and luxury aesthetics. Compare verified Scottsdale-area providers across Old Town, North Scottsdale, and Paradise Valley.",
    neighborhoods: ["Old Town Scottsdale", "North Scottsdale", "Paradise Valley", "South Scottsdale"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Morpheus8",
      "Laser Hair Removal",
      "CoolSculpting",
      "Microneedling",
      "HydraFacial",
      "PRP Injections",
    ],
  },
  {
    slug: "phoenix",
    name: "Phoenix",
    stateSlug: "arizona",
    stateAbbr: "AZ",
    stateName: "Arizona",
    cityMatchers: ["Phoenix", "Tempe", "Mesa", "Chandler", "Gilbert", "Glendale"],
    description:
      "Find verified MedSpas in Phoenix, AZ. Compare top providers across Phoenix, Tempe, Mesa, Chandler, and Gilbert for Botox, fillers, and laser treatments.",
    intro:
      "Phoenix and the surrounding Valley host hundreds of medical spas, from boutique injectors in Arcadia to full-service aesthetic clinics in Chandler and Gilbert. Browse verified Phoenix-area providers and compare reviews, treatments, and hours.",
    neighborhoods: ["Arcadia", "Biltmore", "Ahwatukee", "Tempe", "Mesa", "Chandler", "Gilbert", "Glendale"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Laser Hair Removal",
      "Microneedling",
      "CoolSculpting",
      "Semaglutide / GLP-1",
      "IV Therapy",
      "HydraFacial",
    ],
  },
  {
    slug: "san-diego",
    name: "San Diego",
    stateSlug: "california",
    stateAbbr: "CA",
    stateName: "California",
    cityMatchers: ["San Diego", "La Jolla", "Carlsbad", "Encinitas", "Del Mar", "Chula Vista"],
    description:
      "Browse verified MedSpas in San Diego. Compare top providers in La Jolla, Del Mar, Carlsbad, and Downtown San Diego for Botox, fillers, and laser.",
    intro:
      "San Diego's MedSpa scene blends coastal luxury with science-driven aesthetic medicine. Browse verified providers from La Jolla and Del Mar to Carlsbad, Encinitas, and downtown San Diego.",
    neighborhoods: ["La Jolla", "Del Mar", "Carlsbad", "Encinitas", "Downtown San Diego", "Mission Valley"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Morpheus8",
      "Laser Hair Removal",
      "Microneedling",
      "CoolSculpting",
      "HydraFacial",
      "Sculptra",
    ],
  },
  {
    slug: "seattle",
    name: "Seattle",
    stateSlug: "washington",
    stateAbbr: "WA",
    stateName: "Washington",
    cityMatchers: ["Seattle", "Bellevue", "Kirkland", "Redmond", "Renton"],
    description:
      "Find verified MedSpas in Seattle, WA. Compare top providers in Downtown Seattle, Bellevue, Kirkland, and Redmond for Botox, fillers, and laser.",
    intro:
      "Seattle and the Eastside have a strong MedSpa market focused on injectables, lasers, and skin health. Compare verified Seattle-area providers from Downtown Seattle to Bellevue, Kirkland, and Redmond.",
    neighborhoods: ["Downtown Seattle", "Capitol Hill", "Bellevue", "Kirkland", "Redmond", "Queen Anne"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Microneedling",
      "Laser Hair Removal",
      "HydraFacial",
      "Morpheus8",
      "IPL Photofacial",
      "Chemical Peels",
    ],
  },
  {
    slug: "denver",
    name: "Denver",
    stateSlug: "colorado",
    stateAbbr: "CO",
    stateName: "Colorado",
    cityMatchers: ["Denver", "Cherry Creek", "Englewood", "Centennial", "Lakewood", "Aurora"],
    description:
      "Browse verified MedSpas in Denver, CO. Compare top providers in Cherry Creek, LoDo, and the Tech Center for Botox, fillers, and laser treatments.",
    intro:
      "Denver's MedSpa market has grown rapidly, with strong clusters in Cherry Creek, LoDo, and the Denver Tech Center. Compare verified Denver-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["Cherry Creek", "LoDo", "Highlands", "Denver Tech Center", "Englewood", "Lakewood"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Microneedling",
      "Laser Hair Removal",
      "CoolSculpting",
      "Morpheus8",
      "HydraFacial",
      "IV Therapy",
    ],
  },
  {
    slug: "boston",
    name: "Boston",
    stateSlug: "massachusetts",
    stateAbbr: "MA",
    stateName: "Massachusetts",
    cityMatchers: ["Boston", "Newton", "Brookline", "Cambridge", "Wellesley", "Back Bay"],
    description:
      "Find verified MedSpas in Boston, MA. Compare top providers in Back Bay, Newton, Brookline, and Wellesley for Botox, fillers, and laser.",
    intro:
      "Boston's MedSpa scene is anchored by leading injectors in the Back Bay and a strong cluster of clinics across Newton, Brookline, and Wellesley. Compare verified Boston-area providers and read real patient reviews.",
    neighborhoods: ["Back Bay", "Beacon Hill", "Newton", "Brookline", "Wellesley", "Cambridge"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Microneedling",
      "Laser Hair Removal",
      "HydraFacial",
      "Morpheus8",
      "CoolSculpting",
      "Chemical Peels",
    ],
  },
  {
    slug: "philadelphia",
    name: "Philadelphia",
    stateSlug: "pennsylvania",
    stateAbbr: "PA",
    stateName: "Pennsylvania",
    cityMatchers: ["Philadelphia", "Main Line", "Bryn Mawr", "King of Prussia", "Cherry Hill"],
    description:
      "Browse verified MedSpas in Philadelphia, PA. Compare top providers in Center City, the Main Line, and King of Prussia for Botox, fillers, and laser.",
    intro:
      "Philadelphia and the surrounding Main Line have a deep bench of medical spas focused on injectables, lasers, and skin health. Compare verified providers from Center City to King of Prussia and Cherry Hill.",
    neighborhoods: ["Center City", "Rittenhouse Square", "Main Line", "Bryn Mawr", "King of Prussia"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Microneedling",
      "Laser Hair Removal",
      "HydraFacial",
      "Morpheus8",
      "CoolSculpting",
      "Chemical Peels",
    ],
  },
  {
    slug: "nashville",
    name: "Nashville",
    stateSlug: "tennessee",
    stateAbbr: "TN",
    stateName: "Tennessee",
    cityMatchers: ["Nashville", "Brentwood", "Franklin", "Green Hills"],
    description:
      "Find verified MedSpas in Nashville, TN. Compare top providers in Green Hills, Brentwood, Franklin, and Downtown for Botox, fillers, and laser.",
    intro:
      "Nashville's MedSpa market has expanded rapidly, with strong demand for injectables, body contouring, and weight loss programs. Compare verified Nashville-area providers across Green Hills, Brentwood, and Franklin.",
    neighborhoods: ["Green Hills", "Brentwood", "Franklin", "Downtown Nashville", "The Gulch", "East Nashville"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Semaglutide / GLP-1",
      "Morpheus8",
      "Microneedling",
      "Laser Hair Removal",
      "CoolSculpting",
      "IV Therapy",
    ],
  },
  {
    slug: "orlando",
    name: "Orlando",
    stateSlug: "florida",
    stateAbbr: "FL",
    stateName: "Florida",
    cityMatchers: ["Orlando", "Winter Park", "Lake Mary", "Altamonte Springs", "Dr. Phillips"],
    description:
      "Browse verified MedSpas in Orlando, FL. Compare top providers in Winter Park, Dr. Phillips, and Lake Mary for Botox, fillers, and body contouring.",
    intro:
      "Orlando's MedSpa market is anchored by Winter Park and Dr. Phillips, with a wide range of injectables, lasers, and body sculpting providers. Compare verified Orlando-area MedSpas and read patient reviews.",
    neighborhoods: ["Winter Park", "Dr. Phillips", "Lake Mary", "Altamonte Springs", "Downtown Orlando"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Body Contouring",
      "CoolSculpting",
      "Laser Hair Removal",
      "Microneedling",
      "HydraFacial",
      "Semaglutide / GLP-1",
    ],
  },
  {
    slug: "tampa",
    name: "Tampa",
    stateSlug: "florida",
    stateAbbr: "FL",
    stateName: "Florida",
    cityMatchers: ["Tampa", "St. Petersburg", "Clearwater", "Brandon", "Wesley Chapel"],
    description:
      "Find verified MedSpas in Tampa, FL. Compare top providers in South Tampa, St. Petersburg, and Clearwater for Botox, fillers, and laser.",
    intro:
      "Tampa Bay's MedSpa market spans Tampa, St. Petersburg, and Clearwater with strong demand for injectables, body contouring, and laser treatments. Compare verified Tampa-area providers and book with confidence.",
    neighborhoods: ["South Tampa", "Downtown Tampa", "Westchase", "St. Petersburg", "Clearwater", "Brandon"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "CoolSculpting",
      "Body Contouring",
      "Laser Hair Removal",
      "Microneedling",
      "Semaglutide / GLP-1",
      "HydraFacial",
    ],
  },
  {
    slug: "charlotte",
    name: "Charlotte",
    stateSlug: "north-carolina",
    stateAbbr: "NC",
    stateName: "North Carolina",
    cityMatchers: ["Charlotte", "Ballantyne", "SouthPark", "Matthews", "Concord", "Huntersville"],
    description:
      "Browse verified MedSpas in Charlotte, NC. Compare top providers in SouthPark, Ballantyne, and Uptown for Botox, fillers, and laser.",
    intro:
      "Charlotte's MedSpa market is centered on SouthPark, Ballantyne, and Uptown, with strong demand for injectables and skin treatments. Compare verified Charlotte-area providers and read real patient reviews.",
    neighborhoods: ["SouthPark", "Ballantyne", "Uptown", "Dilworth", "Matthews", "Huntersville"],
    topTreatments: [
      "Botox",
      "Dermal Fillers",
      "Microneedling",
      "Laser Hair Removal",
      "HydraFacial",
      "Morpheus8",
      "CoolSculpting",
      "Chemical Peels",
    ],
  },
  {
    slug: "san-francisco",
    name: "San Francisco",
    stateSlug: "california",
    stateAbbr: "CA",
    stateName: "California",
    cityMatchers: ["San Francisco", "SF", "Daly City", "South San Francisco"],
    description:
      "Find verified MedSpas in San Francisco. Compare top providers in Pacific Heights, the Marina, SoMa, and Union Square for Botox, fillers, and laser.",
    intro:
      "San Francisco's MedSpa market favors precision injectables, lasers, and skin health. Compare verified SF providers across Pacific Heights, the Marina, SoMa, and Union Square.",
    neighborhoods: ["Pacific Heights", "Marina", "SoMa", "Union Square", "Russian Hill", "Noe Valley"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "HydraFacial", "Morpheus8", "IPL Photofacial", "Chemical Peels"],
  },
  {
    slug: "washington-dc",
    name: "Washington, D.C.",
    stateSlug: "maryland",
    stateAbbr: "DC",
    stateName: "Washington, D.C.",
    cityMatchers: ["Washington", "Washington, DC", "Washington DC", "Bethesda", "Chevy Chase", "Arlington", "Alexandria", "McLean"],
    description:
      "Browse verified MedSpas in the Washington, D.C. metro. Compare top providers in Bethesda, Chevy Chase, Arlington, and McLean for Botox, fillers, and laser.",
    intro:
      "The D.C. metro has one of the densest MedSpa markets on the East Coast, anchored by injectors in Bethesda, Chevy Chase, Arlington, and McLean. Compare verified providers across the region.",
    neighborhoods: ["Bethesda", "Chevy Chase", "Arlington", "Alexandria", "McLean", "Tysons", "Georgetown", "Dupont Circle"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "Morpheus8", "CoolSculpting", "HydraFacial", "Sculptra"],
  },
  {
    slug: "minneapolis",
    name: "Minneapolis",
    stateSlug: "minnesota",
    stateAbbr: "MN",
    stateName: "Minnesota",
    cityMatchers: ["Minneapolis", "St. Paul", "Saint Paul", "Edina", "Bloomington", "Maple Grove", "Wayzata"],
    description:
      "Find verified MedSpas in the Minneapolis–St. Paul metro. Compare top providers in Edina, Wayzata, and Downtown for Botox, fillers, and laser.",
    intro:
      "The Twin Cities have a strong MedSpa market across Minneapolis, St. Paul, Edina, and Wayzata. Compare verified providers offering injectables, lasers, and body contouring.",
    neighborhoods: ["Downtown Minneapolis", "Edina", "Wayzata", "St. Paul", "Bloomington", "Maple Grove"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "portland",
    name: "Portland",
    stateSlug: "oregon",
    stateAbbr: "OR",
    stateName: "Oregon",
    cityMatchers: ["Portland", "Lake Oswego", "Beaverton", "Tigard", "Hillsboro"],
    description:
      "Browse verified MedSpas in Portland, OR. Compare top providers in Pearl District, Lake Oswego, and Beaverton for Botox, fillers, and laser.",
    intro:
      "Portland's MedSpa scene blends boutique injectables with wellness-focused clinics. Compare verified Portland-area providers from the Pearl District to Lake Oswego.",
    neighborhoods: ["Pearl District", "Northwest Portland", "Lake Oswego", "Beaverton", "Tigard", "Hillsboro"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "HydraFacial", "Morpheus8", "IV Therapy", "Chemical Peels"],
  },
  {
    slug: "salt-lake-city",
    name: "Salt Lake City",
    stateSlug: "utah",
    stateAbbr: "UT",
    stateName: "Utah",
    cityMatchers: ["Salt Lake City", "Holladay", "Cottonwood Heights", "Murray", "Sandy", "Draper"],
    description:
      "Find verified MedSpas in Salt Lake City, UT. Compare top providers in Downtown, Holladay, Sandy, and Draper for Botox, fillers, and laser.",
    intro:
      "Salt Lake City has one of the fastest-growing MedSpa markets in the Mountain West. Compare verified providers from Downtown to Holladay, Sandy, and Draper.",
    neighborhoods: ["Downtown SLC", "Sugar House", "Holladay", "Cottonwood Heights", "Sandy", "Draper"],
    topTreatments: ["Botox", "Dermal Fillers", "Morpheus8", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Semaglutide / GLP-1", "HydraFacial"],
  },
  {
    slug: "detroit",
    name: "Detroit",
    stateSlug: "michigan",
    stateAbbr: "MI",
    stateName: "Michigan",
    cityMatchers: ["Detroit", "Birmingham", "Bloomfield Hills", "Troy", "Royal Oak", "Grosse Pointe", "Novi"],
    description:
      "Browse verified MedSpas in metro Detroit, MI. Compare top providers in Birmingham, Bloomfield Hills, Troy, and Royal Oak for Botox, fillers, and laser.",
    intro:
      "Metro Detroit has a strong MedSpa cluster anchored by Birmingham, Bloomfield Hills, Troy, and Royal Oak. Compare verified providers and book with confidence.",
    neighborhoods: ["Birmingham", "Bloomfield Hills", "Troy", "Royal Oak", "Novi", "Grosse Pointe"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "kansas-city",
    name: "Kansas City",
    stateSlug: "missouri",
    stateAbbr: "MO",
    stateName: "Missouri",
    cityMatchers: ["Kansas City", "Overland Park", "Leawood", "Olathe", "Lee's Summit", "Independence"],
    description:
      "Find verified MedSpas in Kansas City. Compare top providers in the Plaza, Overland Park, and Leawood for Botox, fillers, and laser.",
    intro:
      "The Kansas City metro spans Missouri and Kansas, with a strong MedSpa cluster in the Plaza, Overland Park, and Leawood. Compare verified providers across the region.",
    neighborhoods: ["Country Club Plaza", "Overland Park", "Leawood", "Lee's Summit", "Olathe", "Independence"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "Morpheus8", "CoolSculpting", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "columbus",
    name: "Columbus",
    stateSlug: "ohio",
    stateAbbr: "OH",
    stateName: "Ohio",
    cityMatchers: ["Columbus", "Dublin", "Westerville", "Powell", "Upper Arlington", "New Albany"],
    description:
      "Browse verified MedSpas in Columbus, OH. Compare top providers in Dublin, New Albany, and Upper Arlington for Botox, fillers, and laser.",
    intro:
      "Columbus has one of the fastest-growing MedSpa markets in the Midwest, with strong clusters in Dublin, New Albany, and Upper Arlington. Compare verified providers and read reviews.",
    neighborhoods: ["Short North", "Dublin", "Westerville", "Powell", "Upper Arlington", "New Albany"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "Morpheus8", "CoolSculpting", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "indianapolis",
    name: "Indianapolis",
    stateSlug: "indiana",
    stateAbbr: "IN",
    stateName: "Indiana",
    cityMatchers: ["Indianapolis", "Carmel", "Fishers", "Zionsville", "Westfield", "Noblesville", "Greenwood"],
    description:
      "Find verified MedSpas in Indianapolis, IN. Compare top providers in Carmel, Fishers, and Zionsville for Botox, fillers, and laser.",
    intro:
      "Indianapolis and the surrounding Hamilton County suburbs (Carmel, Fishers, Zionsville) have a strong MedSpa market. Compare verified providers and book with confidence.",
    neighborhoods: ["Downtown Indianapolis", "Carmel", "Fishers", "Zionsville", "Westfield", "Greenwood"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "memphis",
    name: "Memphis",
    stateSlug: "tennessee",
    stateAbbr: "TN",
    stateName: "Tennessee",
    cityMatchers: ["Memphis", "Germantown", "Collierville", "Cordova", "Bartlett"],
    description:
      "Find verified MedSpas in Memphis, TN. Compare top providers in Germantown, Collierville, and East Memphis for Botox, fillers, and laser.",
    intro:
      "Memphis and the surrounding suburbs of Germantown, Collierville, and Cordova have a growing MedSpa market. Compare verified providers and book with confidence.",
    neighborhoods: ["Germantown", "Collierville", "East Memphis", "Cordova", "Bartlett", "Downtown Memphis"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "birmingham",
    name: "Birmingham",
    stateSlug: "alabama",
    stateAbbr: "AL",
    stateName: "Alabama",
    cityMatchers: ["Birmingham", "Mountain Brook", "Hoover", "Vestavia Hills", "Homewood"],
    description:
      "Browse verified MedSpas in Birmingham, AL. Compare top providers in Mountain Brook, Hoover, and Vestavia Hills for Botox, fillers, and laser.",
    intro:
      "Birmingham's MedSpa market is anchored by Mountain Brook, Hoover, and Vestavia Hills. Compare verified Birmingham-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["Mountain Brook", "Hoover", "Vestavia Hills", "Homewood", "Downtown Birmingham", "Liberty Park"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "richmond",
    name: "Richmond",
    stateSlug: "virginia",
    stateAbbr: "VA",
    stateName: "Virginia",
    cityMatchers: ["Richmond", "Short Pump", "Glen Allen", "Midlothian", "Henrico"],
    description:
      "Find verified MedSpas in Richmond, VA. Compare top providers in Short Pump, the Fan, and Midlothian for Botox, fillers, and laser.",
    intro:
      "Richmond's MedSpa market spans Short Pump, the Fan, Midlothian, and the surrounding Henrico County suburbs. Compare verified providers and read patient reviews.",
    neighborhoods: ["Short Pump", "Glen Allen", "Midlothian", "The Fan", "Henrico", "Mechanicsville"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "buffalo",
    name: "Buffalo",
    stateSlug: "new-york",
    stateAbbr: "NY",
    stateName: "New York",
    cityMatchers: ["Buffalo", "Williamsville", "Amherst", "Clarence", "Orchard Park"],
    description:
      "Browse verified MedSpas in Buffalo, NY. Compare top providers in Williamsville, Amherst, and Clarence for Botox, fillers, and laser.",
    intro:
      "Buffalo's MedSpa market spans Williamsville, Amherst, and the affluent Clarence and Orchard Park suburbs. Compare verified providers and book with confidence.",
    neighborhoods: ["Williamsville", "Amherst", "Clarence", "Orchard Park", "Elmwood Village", "Downtown Buffalo"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "hartford",
    name: "Hartford",
    stateSlug: "connecticut",
    stateAbbr: "CT",
    stateName: "Connecticut",
    cityMatchers: ["Hartford", "West Hartford", "Glastonbury", "Farmington", "Avon", "Simsbury"],
    description:
      "Find verified MedSpas in Hartford, CT. Compare top providers in West Hartford, Glastonbury, and Farmington for Botox, fillers, and laser.",
    intro:
      "Hartford and the surrounding Farmington Valley have a strong MedSpa market across West Hartford, Glastonbury, Farmington, and Avon. Compare verified providers and read reviews.",
    neighborhoods: ["West Hartford", "Glastonbury", "Farmington", "Avon", "Simsbury", "Downtown Hartford"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "albuquerque",
    name: "Albuquerque",
    stateSlug: "new-mexico",
    stateAbbr: "NM",
    stateName: "New Mexico",
    cityMatchers: ["Albuquerque", "Rio Rancho", "Corrales", "Bernalillo"],
    description:
      "Browse verified MedSpas in Albuquerque, NM. Compare top providers in Uptown, the Northeast Heights, and Rio Rancho for Botox, fillers, and laser.",
    intro:
      "Albuquerque's MedSpa market spans Uptown, the Northeast Heights, and Rio Rancho. Compare verified Albuquerque-area providers and book with confidence.",
    neighborhoods: ["Uptown", "Northeast Heights", "Rio Rancho", "Nob Hill", "North Valley", "Corrales"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "boise",
    name: "Boise",
    stateSlug: "idaho",
    stateAbbr: "ID",
    stateName: "Idaho",
    cityMatchers: ["Boise", "Meridian", "Eagle", "Nampa", "Caldwell"],
    description:
      "Find verified MedSpas in Boise, ID. Compare top providers in Meridian, Eagle, and Downtown Boise for Botox, fillers, and laser.",
    intro:
      "Boise's MedSpa market has grown rapidly with strong providers in Meridian, Eagle, and Downtown Boise. Compare verified Boise-area providers and read patient reviews.",
    neighborhoods: ["Downtown Boise", "Meridian", "Eagle", "Nampa", "Caldwell", "Boise Bench"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "des-moines",
    name: "Des Moines",
    stateSlug: "iowa",
    stateAbbr: "IA",
    stateName: "Iowa",
    cityMatchers: ["Des Moines", "West Des Moines", "Clive", "Urbandale", "Ankeny", "Waukee"],
    description:
      "Browse verified MedSpas in Des Moines, IA. Compare top providers in West Des Moines, Clive, and Urbandale for Botox, fillers, and laser.",
    intro:
      "Des Moines' MedSpa market spans West Des Moines, Clive, Urbandale, and Ankeny. Compare verified Des Moines-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["West Des Moines", "Clive", "Urbandale", "Ankeny", "Waukee", "Downtown Des Moines"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "madison",
    name: "Madison",
    stateSlug: "wisconsin",
    stateAbbr: "WI",
    stateName: "Wisconsin",
    cityMatchers: ["Madison", "Middleton", "Fitchburg", "Sun Prairie", "Verona", "Waunakee"],
    description:
      "Find verified MedSpas in Madison, WI. Compare top providers in Middleton, Fitchburg, and the Isthmus for Botox, fillers, and laser.",
    intro:
      "Madison's MedSpa market spans the Isthmus, Middleton, Fitchburg, and the surrounding Dane County suburbs. Compare verified Madison-area providers and read reviews.",
    neighborhoods: ["Middleton", "Fitchburg", "Sun Prairie", "Verona", "Waunakee", "Downtown Madison"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "omaha",
    name: "Omaha",
    stateSlug: "nebraska",
    stateAbbr: "NE",
    stateName: "Nebraska",
    cityMatchers: ["Omaha", "Bellevue", "Papillion", "La Vista", "Elkhorn"],
    description:
      "Browse verified MedSpas in Omaha, NE. Compare top providers in West Omaha, Elkhorn, and Papillion for Botox, fillers, and laser.",
    intro:
      "Omaha's MedSpa market spans West Omaha, Elkhorn, Papillion, and the surrounding suburbs. Compare verified Omaha-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["West Omaha", "Elkhorn", "Papillion", "Bellevue", "La Vista", "Downtown Omaha"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "new-orleans",
    name: "New Orleans",
    stateSlug: "louisiana",
    stateAbbr: "LA",
    stateName: "Louisiana",
    cityMatchers: ["New Orleans", "Metairie", "Kenner", "Mandeville", "Covington"],
    description:
      "Browse verified MedSpas in New Orleans, LA. Compare top providers in the Garden District, Uptown, Metairie, and Mandeville for Botox, fillers, and laser.",
    intro:
      "New Orleans and the surrounding North Shore have a strong MedSpa cluster across Uptown, the Garden District, Metairie, and Mandeville. Compare verified providers and read patient reviews.",
    neighborhoods: ["Garden District", "Uptown", "Metairie", "Mandeville", "Covington", "Lakeview"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "HydraFacial", "Morpheus8", "Lip Fillers"],
  },
  {
    slug: "st-louis",
    name: "St. Louis",
    stateSlug: "missouri",
    stateAbbr: "MO",
    stateName: "Missouri",
    cityMatchers: ["St. Louis", "Saint Louis", "Clayton", "Chesterfield", "Ladue", "Town and Country", "St. Charles"],
    description:
      "Find verified MedSpas in St. Louis, MO. Compare top providers in Clayton, Chesterfield, and Ladue for Botox, fillers, and laser.",
    intro:
      "St. Louis has a strong MedSpa market anchored by Clayton, Chesterfield, and the west county corridor. Compare verified St. Louis-area providers and read patient reviews.",
    neighborhoods: ["Clayton", "Chesterfield", "Ladue", "Town and Country", "Central West End", "St. Charles"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "pittsburgh",
    name: "Pittsburgh",
    stateSlug: "pennsylvania",
    stateAbbr: "PA",
    stateName: "Pennsylvania",
    cityMatchers: ["Pittsburgh", "Sewickley", "Mt. Lebanon", "Cranberry Township", "Wexford", "Upper St. Clair"],
    description:
      "Browse verified MedSpas in Pittsburgh, PA. Compare top providers in Shadyside, Sewickley, and the South Hills for Botox, fillers, and laser.",
    intro:
      "Pittsburgh's MedSpa market spans Shadyside, the South Hills, and the affluent North Hills suburbs of Sewickley, Cranberry, and Wexford. Compare verified providers and read reviews.",
    neighborhoods: ["Shadyside", "Sewickley", "Mt. Lebanon", "Wexford", "Cranberry Township", "Upper St. Clair"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "louisville",
    name: "Louisville",
    stateSlug: "kentucky",
    stateAbbr: "KY",
    stateName: "Kentucky",
    cityMatchers: ["Louisville", "St. Matthews", "Prospect", "Anchorage", "Middletown"],
    description:
      "Find verified MedSpas in Louisville, KY. Compare top providers in St. Matthews, Prospect, and the East End for Botox, fillers, and laser.",
    intro:
      "Louisville's MedSpa market is centered on St. Matthews, the East End, and the surrounding Jefferson and Oldham county areas. Compare verified providers and book with confidence.",
    neighborhoods: ["St. Matthews", "Prospect", "East End", "Middletown", "Highlands", "Anchorage"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "oklahoma-city",
    name: "Oklahoma City",
    stateSlug: "oklahoma",
    stateAbbr: "OK",
    stateName: "Oklahoma",
    cityMatchers: ["Oklahoma City", "Edmond", "Nichols Hills", "Norman", "Moore"],
    description:
      "Browse verified MedSpas in Oklahoma City, OK. Compare top providers in Edmond, Nichols Hills, and Norman for Botox, fillers, and laser.",
    intro:
      "Oklahoma City's MedSpa market spans Edmond, Nichols Hills, and Norman. Compare verified OKC-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["Edmond", "Nichols Hills", "Norman", "Moore", "Bricktown", "Downtown OKC"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "san-antonio",
    name: "San Antonio",
    stateSlug: "texas",
    stateAbbr: "TX",
    stateName: "Texas",
    cityMatchers: ["San Antonio", "Alamo Heights", "Stone Oak", "Boerne", "New Braunfels"],
    description:
      "Browse verified MedSpas in San Antonio, TX. Compare top providers in Alamo Heights, Stone Oak, and the Medical Center for Botox, fillers, and laser.",
    intro:
      "San Antonio's MedSpa market is anchored by Alamo Heights, Stone Oak, and the Medical Center. Compare verified San Antonio-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["Alamo Heights", "Stone Oak", "Medical Center", "The Dominion", "Boerne", "New Braunfels"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "fort-lauderdale",
    name: "Fort Lauderdale",
    stateSlug: "florida",
    stateAbbr: "FL",
    stateName: "Florida",
    cityMatchers: ["Fort Lauderdale", "Boca Raton", "Pompano Beach", "Deerfield Beach", "Plantation", "Weston"],
    description:
      "Find verified MedSpas in Fort Lauderdale, FL. Compare top providers in Las Olas, Boca Raton, and Weston for Botox, fillers, and body contouring.",
    intro:
      "South Florida's MedSpa market between Miami and West Palm includes Fort Lauderdale, Boca Raton, Pompano, and Weston. Compare verified providers and read patient reviews.",
    neighborhoods: ["Las Olas", "Boca Raton", "Pompano Beach", "Deerfield Beach", "Plantation", "Weston"],
    topTreatments: ["Botox", "Dermal Fillers", "Body Contouring", "Lip Fillers", "CoolSculpting", "Laser Hair Removal", "Microneedling", "Semaglutide / GLP-1"],
  },
  {
    slug: "sacramento",
    name: "Sacramento",
    stateSlug: "california",
    stateAbbr: "CA",
    stateName: "California",
    cityMatchers: ["Sacramento", "Roseville", "Folsom", "El Dorado Hills", "Granite Bay", "Elk Grove"],
    description:
      "Browse verified MedSpas in Sacramento, CA. Compare top providers in Roseville, Folsom, and El Dorado Hills for Botox, fillers, and laser.",
    intro:
      "Sacramento's MedSpa market is strong across Roseville, Folsom, and El Dorado Hills. Compare verified Sacramento-area providers for injectables, lasers, and body contouring.",
    neighborhoods: ["Midtown Sacramento", "Roseville", "Folsom", "El Dorado Hills", "Granite Bay", "Elk Grove"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "Morpheus8", "CoolSculpting", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "jacksonville",
    name: "Jacksonville",
    stateSlug: "florida",
    stateAbbr: "FL",
    stateName: "Florida",
    cityMatchers: ["Jacksonville", "Ponte Vedra", "Jacksonville Beach", "St. Augustine", "Orange Park"],
    description:
      "Find verified MedSpas in Jacksonville, FL. Compare top providers in San Marco, Ponte Vedra, and Jacksonville Beach for Botox, fillers, and laser.",
    intro:
      "Jacksonville and the surrounding First Coast — Ponte Vedra, Jax Beach, and St. Augustine — have a strong MedSpa cluster. Compare verified providers and read patient reviews.",
    neighborhoods: ["San Marco", "Riverside", "Ponte Vedra", "Jacksonville Beach", "St. Augustine", "Orange Park"],
    topTreatments: ["Botox", "Dermal Fillers", "Body Contouring", "Laser Hair Removal", "CoolSculpting", "Microneedling", "HydraFacial", "Semaglutide / GLP-1"],
  },
  {
    slug: "cincinnati",
    name: "Cincinnati",
    stateSlug: "ohio",
    stateAbbr: "OH",
    stateName: "Ohio",
    cityMatchers: ["Cincinnati", "Hyde Park", "Mason", "West Chester", "Blue Ash", "Montgomery"],
    description:
      "Browse verified MedSpas in Cincinnati, OH. Compare top providers in Hyde Park, Mason, and Blue Ash for Botox, fillers, and laser.",
    intro:
      "Cincinnati's MedSpa market is anchored by Hyde Park and the northern suburbs of Mason, Blue Ash, and West Chester. Compare verified Cincinnati-area providers and read reviews.",
    neighborhoods: ["Hyde Park", "Mason", "West Chester", "Blue Ash", "Montgomery", "Kenwood"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "CoolSculpting", "Morpheus8", "HydraFacial", "Chemical Peels"],
  },
  {
    slug: "raleigh",
    name: "Raleigh",
    stateSlug: "north-carolina",
    stateAbbr: "NC",
    stateName: "North Carolina",
    cityMatchers: ["Raleigh", "Cary", "Durham", "Chapel Hill", "Apex", "Wake Forest"],
    description:
      "Browse verified MedSpas in the Raleigh–Durham, NC area. Compare top providers in Raleigh, Cary, Durham, and Chapel Hill for Botox, fillers, and laser.",
    intro:
      "The Research Triangle has a fast-growing MedSpa market across Raleigh, Cary, Durham, and Chapel Hill. Compare verified providers and read patient reviews.",
    neighborhoods: ["North Raleigh", "Downtown Raleigh", "Cary", "Durham", "Chapel Hill", "Apex"],
    topTreatments: ["Botox", "Dermal Fillers", "Microneedling", "Laser Hair Removal", "Morpheus8", "CoolSculpting", "HydraFacial", "Chemical Peels"],
  },
];

/** Look up a city by stateSlug + citySlug. */
export function getCity(stateSlug: string, citySlug: string): CityData | undefined {
  return CITIES.find((c) => c.stateSlug === stateSlug && c.slug === citySlug);
}

/** Cities for a given state slug. Used by StateBrowse for internal linking. */
export function getCitiesForState(stateSlug: string): CityData[] {
  return CITIES.filter((c) => c.stateSlug === stateSlug);
}

/**
 * Per-city pricing multiplier vs the national average shown in the cost guide.
 * Used by CityTreatmentBrowse to render city-specific pricing copy and capture
 * "cost of [treatment] in [city]" search intent.
 */
export const CITY_PRICE_MULTIPLIER: Record<string, number> = {
  // Premium metros — 15–30% above national avg
  "new-york/new-york-city": 1.25,
  "california/los-angeles": 1.2,
  "california/san-francisco": 1.25,
  "florida/miami": 1.15,
  "maryland/washington-dc": 1.2,
  "massachusetts/boston": 1.15,
  // Major metros — 5–10% above national avg
  "illinois/chicago": 1.05,
  "texas/dallas": 1.05,
  "texas/houston": 1.05,
  "texas/austin": 1.05,
  "georgia/atlanta": 1.05,
  "washington/seattle": 1.1,
  "colorado/denver": 1.05,
  "nevada/las-vegas": 1.0,
  "arizona/scottsdale": 1.1,
  "arizona/phoenix": 1.0,
  "california/san-diego": 1.1,
  "pennsylvania/philadelphia": 1.05,
  "florida/orlando": 1.0,
  "florida/tampa": 1.0,
  "north-carolina/charlotte": 1.0,
  // Standard metros — at or slightly below national avg
  "tennessee/nashville": 1.0,
  "minnesota/minneapolis": 1.0,
  "oregon/portland": 1.0,
  "utah/salt-lake-city": 0.95,
  "michigan/detroit": 0.95,
  "missouri/kansas-city": 0.95,
  "ohio/columbus": 0.95,
  "indiana/indianapolis": 0.95,
  "north-carolina/raleigh": 1.0,
  "texas/san-antonio": 1.0,
  "florida/fort-lauderdale": 1.1,
  "california/sacramento": 1.05,
  "florida/jacksonville": 1.0,
  "ohio/cincinnati": 0.95,
  "louisiana/new-orleans": 1.0,
  "missouri/st-louis": 0.95,
  "pennsylvania/pittsburgh": 0.95,
  "kentucky/louisville": 0.95,
  "oklahoma/oklahoma-city": 0.95,
  "tennessee/memphis": 0.95,
  "alabama/birmingham": 0.95,
  "virginia/richmond": 1.0,
  "new-york/buffalo": 0.95,
  "connecticut/hartford": 1.05,
  "new-mexico/albuquerque": 0.95,
  "idaho/boise": 0.95,
  "iowa/des-moines": 0.95,
  "wisconsin/madison": 0.95,
  "nebraska/omaha": 0.95,
};

export function getCityPriceMultiplier(
  stateSlug: string,
  citySlug: string,
): number {
  return CITY_PRICE_MULTIPLIER[`${stateSlug}/${citySlug}`] ?? 1.0;
}

/** Match a Listing.city value to a CityData entry (case-insensitive). */
export function matchListingToCity(
  listingCity: string | null | undefined,
  listingState: string | null | undefined
): CityData | undefined {
  if (!listingCity) return undefined;
  const c = listingCity.trim().toLowerCase();
  return CITIES.find((city) => {
    if (
      listingState &&
      city.stateAbbr.toLowerCase() !== listingState.trim().toLowerCase() &&
      city.stateName.toLowerCase() !== listingState.trim().toLowerCase()
    ) {
      return false;
    }
    return city.cityMatchers.some((m) => m.toLowerCase() === c);
  });
}
